import React, { useState } from "react";
import {
    Typography,
    Box,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Button,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    FormControlLabel,
    Switch,
    Tooltip,
    AlertColor,
    MenuItem
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { getConfigForRegion, Regions, ApiLicenseData } from "../../config";
import CustomSnackbar from "../CustomSnackbar";

interface SystemLicensesProps {
    systemId: string;
    region: string;
}

interface LicenseFormData {
    id?: number;
    systemId: string;
    featureId: number | string;
    expiryDate: string;
    isActive: boolean;
}

const SystemLicenses: React.FC<SystemLicensesProps> = ({ systemId, region }) => {
    const { headersWithAuth } = useAuth();
    const [licenses, setLicenses] = useState<ApiLicenseData[]>([]);
    const [features, setFeatures] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [licenseToDelete, setLicenseToDelete] = useState<number | null>(null);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState<AlertColor>("success");

    const [formData, setFormData] = useState<LicenseFormData>({
        systemId: systemId,
        featureId: "",
        expiryDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
        isActive: true
    });

    const currentRegion = Object.values(Regions).find((r) => r.displayName === region) || Regions.Canada;
    const currentConfig = getConfigForRegion(currentRegion);

    const fetchFeatures = async () => {
        try {
            const response = await axios.post(
                currentConfig.api.LICENSE_FEATURES,
                {},
                headersWithAuth
            );
            console.log("Features response:", response.data);
            setFeatures(response.data || []);
        } catch (error: any) {
            console.error("Error fetching features:", error);
        }
    };

    const fetchLicenses = async () => {
        setIsLoading(true);
        try {
            console.log("Fetching licenses for systemId:", systemId, "region:", region);
            const response = await axios.post(
                currentConfig.api.LICENSES,
                { systemId },
                headersWithAuth
            );
            console.log("Licenses response:", response.data);
            // Extract licenses array from response and filter out null licenseIds
            const licensesData = response.data?.licenses || response.data || [];
            const validLicenses = licensesData.filter((license: any) => license.licenseId !== null);
            setLicenses(validLicenses);
        } catch (error: any) {
            console.error("Error fetching licenses:", error);
            const errorMsg = error?.response?.data?.message 
                || error?.response?.data?.error
                || error?.message 
                || "Failed to fetch licenses";
            setSnackbarMessage(errorMsg);
            setSnackbarSeverity("error");
            setOpenSnackbar(true);
        } finally {
            setIsLoading(false);
        }
    };

    React.useEffect(() => {
        fetchFeatures();
        fetchLicenses();
    }, [systemId, region]);

    const handleOpenDialog = (license?: any) => {
        if (license) {
            setIsEditMode(true);
            setFormData({
                id: license.licenseId || license.id,
                systemId: license.systemId,
                featureId: license.featureId || "",
                expiryDate: license.expiryDate ? new Date(license.expiryDate).toISOString().split("T")[0] : new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
                isActive: license.enabled !== undefined ? license.enabled : license.isActive
            });
        } else {
            setIsEditMode(false);
            setFormData({
                systemId: systemId,
                featureId: "",
                expiryDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
                isActive: true
            });
        }
        setIsDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setIsDialogOpen(false);
        setIsEditMode(false);
    };

    const handleFormChange = (field: keyof LicenseFormData, value: any) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSubmit = async () => {
        try {
            setIsLoading(true);
            const endpoint = isEditMode ? currentConfig.api.UPDATE_LICENSE : currentConfig.api.CREATE_LICENSE;
            
            const payload = isEditMode
                ? {
                      licenseId: formData.id,
                      featureId: Number(formData.featureId),
                      expiryDate: new Date(formData.expiryDate).toISOString(),
                      enabled: formData.isActive
                  }
                : {
                      systemId: formData.systemId,
                      featureId: Number(formData.featureId),
                      expiryDate: new Date(formData.expiryDate).toISOString()
                  };

            console.log("Sending license request:", { endpoint, payload });
            const response = await axios.post(endpoint, payload, headersWithAuth);
            console.log("License response:", response.data);

            // Check for error in response data (following the pattern from System page)
            const errorCode = response?.data?.errorCode || (Array.isArray(response?.data) && response?.data[0]?.errorCode);
            const errorMessage = response?.data?.message || (Array.isArray(response?.data) && response?.data[0]?.message);

            if (errorCode || response?.status >= 400) {
                throw new Error(errorMessage || `Request failed with status ${response?.status}`);
            }
            
            setSnackbarMessage(
                isEditMode ? "License updated successfully!" : "License created successfully!"
            );
            setSnackbarSeverity("success");
            setOpenSnackbar(true);
            handleCloseDialog();
            fetchLicenses();
        } catch (error: any) {
            console.error("Error saving license:", error);
            const errorMsg = error?.response?.data?.message 
                || error?.response?.data?.error
                || error?.message 
                || "Failed to save license";
            setSnackbarMessage(errorMsg);
            setSnackbarSeverity("error");
            setOpenSnackbar(true);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDelete = async (licenseId: number) => {
        setLicenseToDelete(licenseId);
        setDeleteDialogOpen(true);
    };

    const confirmDelete = async () => {
        if (!licenseToDelete) return;

        try {
            setIsLoading(true);
            await axios.post(
                currentConfig.api.DELETE_LICENSE,
                { licenseId: licenseToDelete },
                headersWithAuth
            );
            
            setSnackbarMessage("License deleted successfully!");
            setSnackbarSeverity("success");
            setOpenSnackbar(true);
            fetchLicenses();
        } catch (error) {
            console.error("Error deleting license:", error);
            setSnackbarMessage("Failed to delete license");
            setSnackbarSeverity("error");
            setOpenSnackbar(true);
        } finally {
            setIsLoading(false);
            setDeleteDialogOpen(false);
            setLicenseToDelete(null);
        }
    };

    const cancelDelete = () => {
        setDeleteDialogOpen(false);
        setLicenseToDelete(null);
    };

    const formatDateTime = (dateString: string) => {
        if (!dateString) return "-";
        try {
            // Extract date without timezone conversion to avoid day shift
            const date = dateString.split("T")[0];
            return new Date(date + "T00:00:00").toLocaleDateString();
        } catch (e) {
            return dateString;
        }
    };

    const handleSnackbarClose = () => {
        setOpenSnackbar(false);
    };

    return (
        <Box
            sx={{
                backgroundColor: "#f9f9f9",
                p: 2,
                borderRadius: 2,
                boxShadow: 1,
                maxWidth: "100%"
            }}
        >
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                    Licenses
                </Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                    <Tooltip title="Refresh licenses">
                        <IconButton
                            onClick={fetchLicenses}
                            color="primary"
                            size="small"
                            disabled={isLoading}
                        >
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenDialog()}
                        disabled={isLoading}
                    >
                        Add License
                    </Button>
                </Box>
            </Box>

            {isLoading && (
                <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
                    <CircularProgress />
                </Box>
            )}

            {!isLoading && licenses.length === 0 && (
                <Box sx={{ p: 2, textAlign: "center" }}>
                    <Typography>No licenses found for this system.</Typography>
                </Box>
            )}

            {!isLoading && licenses.length > 0 && (
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Feature Name</TableCell>
                                <TableCell>Expiry Date</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {licenses.map((license: any) => (
                                <TableRow key={license.licenseId || license.id} hover>
                                    <TableCell>{license.featureName || license.name}</TableCell>
                                    <TableCell>{license.expiryDate ? formatDateTime(license.expiryDate) : "-"}</TableCell>
                                    <TableCell>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: (license.enabled !== undefined ? license.enabled : license.isActive) ? "success.main" : "text.secondary",
                                                fontWeight: (license.enabled !== undefined ? license.enabled : license.isActive) ? 600 : 400
                                            }}
                                        >
                                            {(license.enabled !== undefined ? license.enabled : license.isActive) ? "Active" : "Inactive"}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Tooltip title="Edit">
                                            <IconButton
                                                size="small"
                                                color="primary"
                                                onClick={() => handleOpenDialog(license)}
                                            >
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete">
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => handleDelete(license.licenseId || license.id)}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* License Form Dialog */}
            <Dialog open={isDialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>{isEditMode ? "Edit License" : "Create New License"}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
                        <TextField
                            select
                            label="Feature"
                            fullWidth
                            required
                            value={formData.featureId}
                            onChange={(e) => handleFormChange("featureId", e.target.value)}
                        >
                            <MenuItem value="">Select a feature</MenuItem>
                            {features.map((feature) => (
                                <MenuItem key={feature.id} value={feature.id}>
                                    {feature.name || feature.featureName || `Feature ${feature.id}`}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            label="Expiry Date"
                            type="date"
                            fullWidth
                            required
                            value={formData.expiryDate}
                            onChange={(e) => handleFormChange("expiryDate", e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                        {isEditMode && (
                            <FormControl component="fieldset">
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={formData.isActive}
                                            onChange={(e) => handleFormChange("isActive", e.target.checked)}
                                            color="primary"
                                        />
                                    }
                                    label="Active"
                                />
                            </FormControl>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        color="primary"
                        disabled={!formData.featureId || !formData.expiryDate || isLoading}
                    >
                        {isEditMode ? "Update" : "Create"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={cancelDelete} maxWidth="xs" fullWidth>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <Typography>Are you sure you want to delete this license?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={cancelDelete} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={confirmDelete} color="error" variant="contained" disabled={isLoading}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            <CustomSnackbar
                open={openSnackbar}
                handleClose={handleSnackbarClose}
                message={snackbarMessage}
                severity={snackbarSeverity}
            />
        </Box>
    );
};

export default SystemLicenses;
